<template>
  <div
    class="br-add-document"
    :class="{ 'br-add-document--embedded': fromAdmin }"
  >
    <BrScanLoadingModal
      :show="scanning || saving"
      :message="scanning ? $t('lbl_EMPBE_BR_Scan_Loading') : $t('lbl_EMPBE_BR_Save_Loading')"
    />

    <div class="br-add-document__container">
      <nav class="br-add-document__breadcrumb" aria-label="breadcrumb">
        <a
          v-if="fromAdmin"
          href="#"
          class="br-add-document__crumb-link"
          @click.prevent="goBackFromAdminAdd"
        >
          {{ $t('lbl_EMPBE_BusinessDocuments') }}
        </a>
        <router-link
          v-else
          to="/businessDocuments/default"
          class="br-add-document__crumb-link"
        >
          {{ $t('lbl_EMPBE_BusinessDocuments') }}
        </router-link>
        <feather type="chevron-right" :size="20" class="br-add-document__crumb-sep" />
        <span class="br-add-document__crumb-current">{{ $t('lbl_EMPBE_BR_AddDocument') }}</span>
      </nav>

      <div class="br-add-document__card br-add-document__card--steps">
        <div class="br-add-document__title-block">
          <component
            :is="fromAdmin ? 'h2' : 'h1'"
            class="br-add-document__title"
          >
            {{ $t('lbl_EMPBE_BR_SubmitPage_Title') }}
          </component>
          <p class="br-add-document__subtitle" v-html="pageSubtitle"></p>
        </div>
        <BrSubmitSteps :current-step="currentStep" />
      </div>

      <BrUploadSection
        v-if="currentStep === 'upload'"
        ref="uploadSection"
        :chinese-company-name="displayNameZh"
        :english-company-name="displayNameEn"
        :info-icon="brLicenseAssets.infoIcon"
        :example-images="exampleImages"
        :is-drag-over="isDragOver"
        :scan-alert-keys="scanBlockerKeys"
        :selected-file-name="selectedFileName"
        @dragover="isDragOver = true"
        @dragleave="isDragOver = false"
        @drop="onDrop"
        @pick-file="triggerFileInput"
        @file-change="onFileChange"
        @preview-example="previewExample"
      />

      <div v-else-if="currentStep === 'confirm'" class="br-add-document__card br-add-document__card--confirm">
        <BrConfirmSubmit
          :preview-url="previewUrl"
          :file-name="selectedFileName"
          :is-pdf="isConfirmPreviewPdf"
          :form="confirmForm"
          :display-name-en="displayNameEn"
          :display-name-zh="displayNameZh"
          :scan-alert-keys="scanBlockerKeys"
          :scan-blocker-keys="scanBlockerKeys"
          :scan-reset-token="scanResetToken"
          :submitting="saving"
          @submit="submitForReview"
          @reupload="handleReupload"
          @preview="previewConfirmImage"
        />
      </div>

      <div v-else-if="currentStep === 'pending'" class="br-add-document__card br-add-document__card--pending">
        <BrPendingApproval @contact="handleContactUs" />
      </div>

      <div v-else-if="currentStep === 'rejected'" class="br-add-document__card br-add-document__card--result">
        <BrRejectedApproval
          :reason="rejectReason"
          @contact="handleContactUs"
          @reupload="resetUploadState"
        />
      </div>

      <!-- Step 2 重新上传：确认页无 UploadSection，用隐藏 input 弹选文件且不离开 Step 2 -->
      <input
        ref="confirmReuploadInput"
        type="file"
        class="br-add-document__file-input"
        :accept="uploadAccept"
        @change="onFileChange"
      />
    </div>

    <BrImagePreviewModal
      v-model="showPreview"
      :src="previewSrc"
      :title="previewTitle"
      :item="previewItem"
      :appearance="fromAdmin ? 'admin' : 'supplier'"
      :show-reupload="previewCanReupload"
      @reupload="handleReupload"
    />
    <BrConfirmModal
      :visible="showDuplicatePendingConfirm"
      :title="$t('lbl_EMPBE_BR_DuplicatePending_Title')"
      :body="$t('lbl_EMPBE_BR_DuplicatePending_Body')"
      :cancel-label="$t('lbl_EMPBE_BR_DuplicatePending_Cancel')"
      :confirm-label="$t('lbl_EMPBE_BR_DuplicatePending_Submit')"
      :submitting="saving"
      :close-on-scrim="false"
      @cancel="closeDuplicatePendingConfirm"
      @confirm="confirmDuplicatePendingSubmit"
    />
    <BrConfirmModal
      :visible="showSubmitSuccess"
      :title="$t('lbl_EMPBE_BR_Submit_Success')"
      :body="$t('lbl_EMPBE_BR_SubmitSuccess_Body')"
      :confirm-label="$t('lbl_EMPBE_BR_SubmitSuccess_Ok')"
      hide-cancel
      :close-on-scrim="false"
      @cancel="closeSubmitSuccess"
      @confirm="closeSubmitSuccess"
    />
    <BrContactUsDrawer v-if="!fromAdmin" ref="contactUsDrawer" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import BrUploadSection from './components/BrUploadSection.vue';
import BrSubmitSteps from './components/BrSubmitSteps.vue';
import BrConfirmSubmit from './components/BrConfirmSubmit.vue';
import BrScanLoadingModal from './components/BrScanLoadingModal.vue';
import BrPendingApproval from './components/BrPendingApproval.vue';
import BrRejectedApproval from './components/BrRejectedApproval.vue';
import BrImagePreviewModal from './components/BrImagePreviewModal.vue';
import BrContactUsDrawer from './components/BrContactUsDrawer.vue';
import BrConfirmModal from './components/BrConfirmModal.vue';
import brAddDocumentFlow from './mixins/brAddDocumentFlow';
import {
  loadBrSubmissionStatus,
  shouldLockSidebarForBr,
  acknowledgeBrStatusNotification,
  brRuntimeState,
} from '../../constants/businessRegistration';
import {
  BR_STATUS,
  getLatestBrSubmission,
  getItemStatusCode,
  getBrSubmissionApprovalId,
  isBrResultNotifyPending,
  resolveBrRejectReason,
} from './utils/brDocument';
import { BR_LICENSE_ASSETS } from '../../constants/brLicenseAssets';
import { BR_UPLOAD_ACCEPT } from '../../constants/brConfig';
import { BusinessRegistrationService } from '../../_services';
import { isBrPdfFile } from './utils/brOcr';
import {
  resolveBrCompanyNameEn,
  resolveBrCompanyNameZh,
} from './utils/brCompanyNames';

function positiveInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export default {
  name: 'BusinessDocumentsAdd',
  mixins: [brAddDocumentFlow],
  components: {
    BrUploadSection,
    BrSubmitSteps,
    BrConfirmSubmit,
    BrScanLoadingModal,
    BrPendingApproval,
    BrRejectedApproval,
    BrImagePreviewModal,
    BrContactUsDrawer,
    BrConfirmModal,
  },
  props: {
    fromAdmin: { type: Boolean, default: false },
    empCompanyId: { type: [String, Number], default: null },
    accountId: { type: [String, Number], default: null },
    /** edit | approval；管理端新增执照走 AI（approval）；更名仍可传 edit 一遍过 */
    submitType: { type: String, default: '' },
    /** 管理端：Summary 为空时回退公司名 */
    fallbackNameEn: { type: String, default: '' },
    fallbackNameZh: { type: String, default: '' },
    /** 管理端：已由 loadAdminBrDocuments 拉好的列表，避免再打 GetList */
    seedDocumentList: { type: Array, default: () => [] },
  },
  data() {
    return {
      currentStep: 'upload',
      isDragOver: false,
      showPreview: false,
      previewSrc: '',
      previewItem: null,
      previewTitle: '',
      /** 确认页预览可重新上传；示例图预览不可 */
      previewCanReupload: false,
      brLicenseAssets: BR_LICENSE_ASSETS,
      exampleImages: BR_LICENSE_ASSETS.examples.map((src) => ({ src })),
      rejectReason: '',
      /** 管理端代传：本地 summary，避免污染供应商会话 brRuntimeState */
      adminSummary: null,
    };
  },
  computed: {
    ...mapState('authentication', ['user']),
    targetEmpCompanyId() {
      return this.fromAdmin ? positiveInt(this.empCompanyId) : 0;
    },
    targetAccountId() {
      if (!this.fromAdmin) {
        return 0;
      }
      return positiveInt(this.accountId) || this.targetEmpCompanyId;
    },
    summarySource() {
      return this.fromAdmin ? (this.adminSummary || {}) : (brRuntimeState.summary || {});
    },
    displayNameEn() {
      return resolveBrCompanyNameEn({
        summary: this.summarySource,
        user: this.fromAdmin ? null : this.user,
        fallback: this.fromAdmin ? this.fallbackNameEn : '',
      });
    },
    displayNameZh() {
      return resolveBrCompanyNameZh({
        summary: this.summarySource,
        user: this.fromAdmin ? null : this.user,
        langId: localStorage.getItem('currentLangId'),
        fallback: this.fromAdmin ? this.fallbackNameZh : '',
      });
    },
    brSidebarLocked() {
      if (this.fromAdmin) {
        return false;
      }
      return shouldLockSidebarForBr(this.user);
    },
    pageSubtitle() {
      if (this.brSidebarLocked) {
        return this.$t('lbl_EMPBE_BR_SubmitPage_Subtitle_Locked');
      }
      return this.$t('lbl_EMPBE_BR_SubmitPage_Subtitle');
    },
    uploadAccept() {
      return BR_UPLOAD_ACCEPT;
    },
    isConfirmPreviewPdf() {
      return isBrPdfFile(this.selectedFile)
        || isBrPdfFile(this.selectedFileName)
        || isBrPdfFile(this.previewUrl)
        || isBrPdfFile(this.ossMediaUrl);
    },
  },
  async mounted() {
    if (this.fromAdmin) {
      await this.loadAdminTargetSummary();
      return;
    }
    await loadBrSubmissionStatus(true);
    this.restoreStepFromApi();
  },
  methods: {
    goBackFromAdminAdd() {
      this.$emit('hide-add');
    },
    async loadAdminTargetSummary() {
      if (!this.targetEmpCompanyId) {
        return;
      }
      try {
        const langId = Number(localStorage.getItem('currentLangId')) || 1252;
        const res = await BusinessRegistrationService.GetSummary({
          empCompanyId: this.targetEmpCompanyId,
          langId,
        });
        this.adminSummary = res?.isSuccessful ? (res.data || null) : null;
      } catch (e) {
        this.adminSummary = null;
      }
    },
    /** 管理端代传时附带目标公司 */
    withTargetCompany(payload = {}) {
      if (!this.fromAdmin || !this.targetEmpCompanyId) {
        return payload;
      }
      return {
        ...payload,
        empCompanyId: this.targetEmpCompanyId,
        accountId: this.targetAccountId || this.targetEmpCompanyId,
      };
    },
    restoreStepFromApi() {
      if (!this.brSidebarLocked) {
        return;
      }
      const latest = getLatestBrSubmission(brRuntimeState.documentList);
      if (!latest) {
        return;
      }
      const code = getItemStatusCode(latest);
      // pending 不再强制回退；仅恢复驳回结果页
      if (code !== BR_STATUS.REJECTED) {
        return;
      }
      this.currentStep = 'rejected';
      this.rejectReason = resolveBrRejectReason(latest);
      if (isBrResultNotifyPending(latest)) {
        acknowledgeBrStatusNotification(
          this.user,
          'rejected',
          brRuntimeState.documentList,
          getBrSubmissionApprovalId(latest),
        );
      }
    },
    handleContactUs() {
      this.$refs.contactUsDrawer?.open();
    },
    previewConfirmImage() {
      if (!this.previewUrl) {
        return;
      }
      if (this.isConfirmPreviewPdf) {
        window.open(this.previewUrl, '_blank', 'noopener,noreferrer');
        return;
      }
      const form = this.confirmForm || {};
      this.previewSrc = this.previewUrl;
      this.previewTitle = '';
      this.previewCanReupload = true;
      // 确认页预览：展示当前表单字段（无审批状态 / 提交时间 / ID）
      this.previewItem = {
        companyName: form.registeredCompanyName || '',
        registrationNo: form.registrationNo || '',
        issueDate: form.startDate || '',
        expiryDate: form.hasExpirationDate ? (form.expirationDate || '') : '',
        hasExpirationDate: form.hasExpirationDate === true,
      };
      this.showPreview = true;
    },
    previewExample(example) {
      this.previewSrc = example.src;
      this.previewTitle = this.$t('lbl_EMPBE_BR_Example_Label');
      this.previewCanReupload = false;
      this.previewItem = null;
      this.showPreview = true;
    },
  },
};
</script>

<style lang="scss">
@import './styles/br-shared.scss';
@import './styles/_br-add-flow.scss';
</style>
